<template lang="pug">
div
  ValidationObserver(ref="dialogObserver")
    .time-span-popover.time_entry_form
      .row
        .col-sm-12
          label.control-label.is-block {{ timeSpanLabel }}
      .row
        AppDatetimeRangePicker(
          v-model="datetimeRange"
          type="popover"
          :min-date="minDate"
          :max-date="maxDate"
          :disable-date="isNew"
          :start-week-on="startWeekOn"
          :extra-picker-classes="extraPickerClasses"
          :timezone="currentZone.name"
          :current-zone="currentZone"
          :move-stop-in-parallel="isNew"
        )
      .row
        .col-xs-6.pull-left
          AppDurationInput.duration.time_entry_duration(
            v-model="duration"
            required
            validate-day
            @input="updateDuration"
          )
        .col-xs-6
          .form-col.pull-left
            label.control-label
            .form-group.boolean.optional.time_entry_billable
              .checkbox
                label
                  AppCheckbox.form-group.boolean.optional.time_entry_billable.pl-0.pt-5(v-model="billable")
                    span Billable
      .row
        .col-xs-12
          TimeEntriesTextarea(
            v-model="note"
            name="note"
            label="Reason"
            :placeholder="`${requiredReasonPlaceholder} (ex: Forgot to start timer)`"
            :required="reasonRequiredForTimeEntry"
          )
          TimeEntriesTextarea(
            v-if="isNew && ownActivity"
            v-model="workNote"
            name="work_note"
            label="Note"
            placeholder="What were you working on? (ex: Responding to customers)"
            icon="hi-group-by"
          )

      .row
        .col-xs-12
          button.btn.btn-success.btn-block.btn-sm(@click="save") Apply
      .curtain(v-show="saving")
        .spinner-wrapper
          span.hi.hi-spinner.hi-32.text-blue

  // Confirmation dialog for unlocking time
  TimeEntriesOverrideDialog(
    v-model:show="showOverrideDialog"
    :type="type"
    :time-entry="timeEntry"
    @submit="save"
    @close="handleOverrideCancel"
  )
  AppConfirm(
    v-if="showProjectBudgetConfirm"
    title="Confirm changes"
    @confirm="save({ allowAboveProjectBudget: true })"
    @cancel="showProjectBudgetConfirm = false"
  )
    | {{ confirmProjectBudgetMessage }}
</template>

<script>
import { mapGetters } from 'vuex';
import { formatDuration, getDuration } from '~/utils/time_utils';
import { TimeEntryForm, TimeEntryOverrideApprovedTimesheet } from './../../mixins';
export default {
  name: 'TimeEntriesFormPopoverForm',
  mixins: [TimeEntryForm, TimeEntryOverrideApprovedTimesheet],
  props: {
    timeEntry: {
      type: Object,
      required: true,
    },
    timeEntries: {
      type: Array,
      required: true,
    },
    show: {
      type: Boolean,
      required: true,
    },
    type: {
      type: String,
      default: 'edit',
      required: false,
    },
  },
  data() {
    return {
      datetimeRange: null,
      duration: null,
      inputtedDuration: null,
      extraPickerClasses: 'form-group string required mr-0',
      projectId: null,
      userId: null,
      taskId: null,
      validationToken: null,
      saving: false,
      showProjectBudgetConfirm: false,
      confirmProjectBudgetMessage: '',
      invalid: false,
      check: {},
      workNote: '',
      note: '',
      billable: true,
      errorMessage: 'There was an error when saving this time entry',
      successMessage: 'Time entry saved',
      showOverrideDialog: false,
      extendingTimeUsingManualTimeRequests: false,
    };
  },
  computed: {
    ...mapGetters([
      'startWeekOn',
      'ownActivity',
      'minDate',
      'maxDate',
      'user',
      'currentZone',
      'getProject',
      'isToday',
      'organizationId',
      'manualTimeApprovalFeature'
    ]),
    blockingTimeEntries() {
      return this.timeEntries;
    },
    isNew() {
      return this.type === 'new';
    },
    project() {
      return this.getProject(this.timeEntry.project_id);
    },
    reasonRequiredForTimeEntry() {
      return this.project && this.project.reason_required;
    },
    requiredReasonPlaceholder() {
      const verb = this.isNew ? 'adding' : 'editing';

      return `Why are you ${verb} this time entry?`;
    },
    timeSpanLabel() {
      return `Time Span (${this.currentZone.abbr})*`;
    },
  },
  watch: {
    datetimeRange() {
      this.duration = getDuration(this.datetimeRange.start, this.datetimeRange.stop);

      if (
        this.manualTimeApprovalFeature &&
        !this.isNew &&
        this.timeEntry &&
        this.timeEntry.tracked < this.duration.asSeconds()
      ) {
        this.extendingTimeUsingManualTimeRequests = true;
      } else {
        this.extendingTimeUsingManualTimeRequests = false;
      }

      this.checkForCollisions();
    },
    show(value) {
      if (value) {
        this.reset();
      }
    },
  },
  created() {
    this.extraPickerClasses = 'form-group string required mr-0';
    this.reset();
  },
  mounted() {
    this.resetValidationState();
  },
  methods: {
    updateDuration(val) {
      const datetime = this.datetimeRange.start.clone().add(val, 's');

      this.datetimeRange = { start: this.datetimeRange.start, stop: datetime };
      this.checkForCollisions();
    },
    checkForCollisions() {
      this.check = {};
      this.dispatchCheck(this.timeEntryParams);
    },
    dispatchCheck(params) {
      const success = (response) => {
        this.checking = false; // enable the save button after checking
        if (response.alert) {
          this.$appMessage(response.alert);
        } else {
          this.validationToken = response.validation_token;
          this.check = response;
        }
        this.denied = !this.check.allowed;
      };

      if (this.extendingTimeUsingManualTimeRequests) {
        params.check_manual_time_request_collision = true;
      }

      this.checking = true; // disable the save button while checking
      this.$store.dispatch('checkTimeEntry', { id: this.timeEntry.id, params }).then(success);
    },
    // NOTE: refactor this function by moving it into the TimeEntryForm mixin as it's the same in popover forms and dialogs
    async save({ allowAboveProjectBudget = false } = {}) {
      const isValid = await this.$refs.dialogObserver.validate();

      if (isValid) {
        const success = this.success;
        const error = () => this.$appMessage(this.errorMessage);
        const fin = () => {
          this.saving = false;
        };

        this.saving = true;
        this.showProjectBudgetConfirm = false;
        const params = Object.assign(this.timeEntryParams, {
          validation_token: this.validationToken,
          allow_above_project_budget: allowAboveProjectBudget,
        });

        if (this.isNew) {
          this.$store.dispatch('createTimeEntry', { params }).then(success, error).then(fin);
        } else {
          if (this.extendingTimeUsingManualTimeRequests) {
            this.$store.dispatch('updateWithManualTimeRequests', { id: this.timeEntry.id, params }).then(success, error).then(fin);
          } else {
            this.$store.dispatch('editTimeEntry', { id: this.timeEntry.id, params }).then(success, error).then(fin);
          }
        }
      }
    },
    // NOTE: refactor this function by moving it into the TimeEntryForm mixin and adding a callback for customization
    success(response) {
      const editedTimeEntry = Object.assign({}, this.timeEntry, {
        start_time: this.datetimeRange.start,
        stop_time: this.datetimeRange.stop,
        duration: this.duration,
        tracked: this.duration.asSeconds(),
        work_note: this.workNote,
        note: this.note,
        billable: this.billable,
      });

      if (response.action === 'confirm-override') {
        this.$store.dispatch('setSelectedTimeEntry', { timeEntry: this.timeEntry });
        this.$store.dispatch('assignAssociatedRecords', {
          id: this.timeEntry.id,
          associatedRecords: response.associated_records,
        });
        this.previousValidationToken = this.validationToken;
        this.timesheetRedirectUrl = response.timesheet_redirect_url;
        this.validationToken = response.validation_token;
        this.showOverrideDialog = true;
      } else if (response.action === 'confirm-project-budget') {
        this.showProjectBudgetConfirm = true;
        this.confirmProjectBudgetMessage = response.message;
      } else if (response.action === 'error-response') {
        const errors = response.errors;

        this.$refs.dialogObserver.setErrors(errors);
        this.$appMessage(response.alert);
        if (!this.isNew && errors.collision) {
          this.$emit('close');
          this.$emit('open-edit-dialog', editedTimeEntry);
        }
      } else {
        this.$appMessage(this.successMessage);
        if (this.isNew) {
          this.$emit('replace-time-entry', editedTimeEntry);
        } else {
          this.$store.dispatch('loadData');
        }

        if (this.timesheetRedirectUrl) {
          this.redirectToTimesheet();
        } else {
          this.$emit('close');
        }
      }
    },
    reset() {
      this.setStartStopDatetime(this.timeEntry.start_time, this.timeEntry.stop_time);
      if (this.isNew) {
        this.setInitialStartStopTimes();
      }
      // Set duration before the input mounts. The range watcher is deferred until after the
      // first render, so the duration field would otherwise register with a null value.
      if (this.datetimeRange?.start && this.datetimeRange?.stop) {
        this.duration = getDuration(this.datetimeRange.start, this.datetimeRange.stop);
      }
      this.userId = this.user.id;
      this.projectId = this.timeEntry.project_id;
      this.taskId = this.timeEntry.task_id;
      this.workNote = '';
      this.note = '';
      this.billable = this.timeEntry.billable;
      this.extendingTimeUsingManualTimeRequests = false;
      this.timesheetRedirectUrl = null;
      this.previousValidationToken = null;
      if (this.$refs.dialogObserver) {
        this.resetValidationState();
      }
    },
    resetValidationState() {
      if (!this.$refs.dialogObserver) {
        return;
      }

      const formatted = this.duration && typeof this.duration.asSeconds === 'function'
        ? formatDuration(this.duration, { showSeconds: 'auto' })
        : '';

      // resetForm restores the value captured when the field first registered. That value is
      // null if the input mounted before duration was set, which makes Apply fail with
      // "can't be blank" until the user edits Duration. Pass the current formatted duration.
      this.$refs.dialogObserver.reset({
        values: { appDurationInput: formatted || '' },
      });
    },
  },
};
</script>
